import React from 'react'

import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import type { PolicyPeriods } from 'modules/payroll_bundle/store/collections/policyPeriods'
import PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

interface PolicyPeriodsContextType {
  policyPeriodCollection: PolicyPeriods
  loadingPolicyPeriodCollection: boolean
  currentPolicyPeriods: PolicyPeriod[]
  policyId: string | null
  setCurrentPolicy: (id: string, periodId?: string) => void
  periodIndex: number
  setPeriodIndex: (index: number) => void
  setPeriodId: (id: string) => void
  currentPeriodStatus: string | undefined
  createNextPeriodForPolicy: (policyId: string) => Promise<void>
  createPreviousPeriodForPolicy: (policyId: string) => Promise<void>
  fetchCurrentPolicyPeriod: () => Promise<void>
  currentPeriod?: PolicyPeriod
  prevPeriod?: PolicyPeriod
}

interface PolicyAndPeriods {
  policyId: string | null
  periods: PolicyPeriod[]
  periodIndex: number
}

const useFetch = createAsyncData((policyPeriodCollection, isBookkeeper) => {
  return policyPeriodCollection.fetch({
    data: { bookkeeper_view: isBookkeeper },
  })
})

const Context = React.createContext<PolicyPeriodsContextType | null>(null)

/**
 * A provider for policy periods.
 *
 * Get the provided value with `usePolicyPeriods`.
 */
export const PolicyPeriodsProvider = ({
  policyPeriodCollection,
  isBookkeeper = false,
  children,
}: {
  policyPeriodCollection: PolicyPeriods
  isBookkeeper?: boolean
  children: React.ReactNode
}) => {
  const loadingPolicyPeriodCollection = useFetch(policyPeriodCollection, isBookkeeper)
  const statuses: Record<string, string> = PolicyPeriod.Statuses()

  const [currentPolicyAndPeriods, setCurrentPolicyAndPeriods] = React.useState<PolicyAndPeriods>({
    policyId: null,
    periods: [],
    periodIndex: 0,
  })

  const setCurrentPolicy = (id: string, periodId?: string) => {
    if (loadingPolicyPeriodCollection) {
      throw new Error(
        'setCurrentPolicy called when policy periods are not yet loaded (loadingPolicyPeriodCollection)'
      )
    }

    // Note that filter returns an array of PolicyPeriod, not a collection
    const periods = policyPeriodCollection.filter({ policy_id: id })

    // Invalid policy ID
    if (!periods.length) {
      setCurrentPolicyAndPeriods((prev) => ({
        ...prev,
        policyId: null,
        periodIndex: 0,
      }))

      return
    }

    let periodIndex = periodId !== undefined ? periods.findIndex((p) => p.id == periodId) : -1
    if (periodIndex === -1) {
      periodIndex = PolicyPeriod.findCurrentOrLatestPeriodIndex(periods)
    }

    setCurrentPolicyAndPeriods({
      policyId: id,
      periods,
      periodIndex,
    })
  }

  const setPeriodIndex = (index: number) => {
    setCurrentPolicyAndPeriods((prev) => ({
      ...prev,
      periodIndex: index,
    }))
  }

  const setPeriodId = (id: string) => {
    const index = currentPolicyAndPeriods.periods.findIndex((period) => period.id == id)

    if (index !== -1) {
      setPeriodIndex(index)
    }
  }

  const filterAndSortPeriods = useUnsafeCallback(
    (policyId: string) => {
      return policyPeriodCollection
        .filter({ policy_id: policyId })
        .sort((p1, p2) => (p1.attributes.starts_on > p2.attributes.starts_on ? 1 : -1))
    },
    [policyPeriodCollection]
  )

  // It also updates the state's periods to include the newly created one and
  // the periodIndex (it's increased by 1 so that it corresponds to the new
  // period). Doing both updates in the same "setState" operation prevents
  // data inconsistencies.
  const createNextPeriodForPolicy = useUnsafeCallback(
    (policyId: string) => {
      return policyPeriodCollection.createNextForPolicy(policyId).then(() => {
        setCurrentPolicyAndPeriods((prev) => ({
          ...prev,
          periods: filterAndSortPeriods(policyId),
          periodIndex: currentPolicyAndPeriods.periodIndex + 1,
        }))
      })
    },
    [currentPolicyAndPeriods, policyPeriodCollection, filterAndSortPeriods]
  )
  const createPreviousPeriodForPolicy = (policyId: string) => {
    return policyPeriodCollection.createPreviousForPolicy(policyId).then(() => {
      setCurrentPolicyAndPeriods((prev) => ({
        ...prev,
        periods: filterAndSortPeriods(policyId),
        periodIndex: 0,
      }))
    })
  }
  const fetchCurrentPolicyPeriod = () => {
    if (currentPolicyAndPeriods.policyId) {
      return policyPeriodCollection.fetch({
        data: { policy_id: currentPolicyAndPeriods.policyId },
      })
    }
    return Promise.resolve()
  }

  const periodIndex = currentPolicyAndPeriods.periodIndex
  const currentPeriod = currentPolicyAndPeriods.periods[periodIndex]
  const previousPeriodIndex = periodIndex - 1
  const currentPolicyPeriods = currentPolicyAndPeriods.periods

  const value: PolicyPeriodsContextType = {
    policyPeriodCollection,
    loadingPolicyPeriodCollection,
    currentPolicyPeriods,
    policyId: currentPolicyAndPeriods.policyId,
    setCurrentPolicy,
    periodIndex,
    setPeriodIndex,
    setPeriodId,
    currentPeriodStatus: currentPeriod ? statuses[currentPeriod.attributes.status] : undefined,
    createNextPeriodForPolicy,
    createPreviousPeriodForPolicy,
    fetchCurrentPolicyPeriod,
    currentPeriod: currentPeriod,
    prevPeriod: previousPeriodIndex >= 0 ? currentPolicyPeriods[previousPeriodIndex] : undefined,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const usePolicyPeriods = (): PolicyPeriodsContextType => {
  const context = React.useContext(Context)

  if (!context) {
    throw new Error('usePolicyPeriods must be used within a PolicyPeriodsProvider')
  }

  return context
}
